import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Heart,
  MessageCircle,
  Bookmark,
  Share2,
  Plus,
  ShoppingBag,
  Sparkles,
  UserPlus,
  UserCheck,
  Volume2,
  VolumeX,
  Play,
  Music2,
  ChevronLeft,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useToast } from "@/components/Toast";
import { useOutfitDraft } from "@/context/OutfitDraftContext";
import { useAuth } from "@/context/AuthContext";
import { PostModerationMenu } from "@/components/PostModerationMenu";

// -----------------------------------------------------------------------------
// TikTok-style Fashion Feed
// -----------------------------------------------------------------------------
// Full-viewport vertical snap-scroll. One post per screen. Right-side action
// rail (like / comment / save / share). Bottom-left caption + creator. Two big
// CTAs at the bottom: GET THIS LOOK and REMIX THIS FIT.
//
// Video posts autoplay (muted) when the post becomes the most-visible item;
// they pause when scrolled away. Tap the screen to toggle mute. Image posts
// behave the same minus the play/pause logic.
// -----------------------------------------------------------------------------

const FEED_TABS = [
  { id: "fyp", label: "For You" },
  { id: "following", label: "Following" },
  { id: "trending", label: "Trending" },
];

function compactNum(n) {
  const v = Number(n) || 0;
  if (v < 1000) return String(v);
  if (v < 1_000_000) return `${(v / 1000).toFixed(v < 10_000 ? 1 : 0)}K`;
  return `${(v / 1_000_000).toFixed(1)}M`;
}

function CommentsSheet({ post, onClose, onCountChange }) {
  const [list, setList] = useState(null);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/feed/posts/${post.id}/comments`);
        setList(data.comments);
      } catch { setList([]); }
    })();
  }, [post.id]);
  const send = async () => {
    if (!text.trim() || posting) return;
    setPosting(true);
    try {
      const { data: c } = await api.post(`/feed/posts/${post.id}/comments`, { text: text.trim() });
      setList((p) => [...(p || []), c]);
      setText("");
      onCountChange?.(post.id, +1);
    } catch { /* swallow — comment will retry on next submit */ }
    finally { setPosting(false); }
  };
  return (
    <div className="tt-sheet-overlay" data-testid="feed-comments-sheet" onClick={onClose}>
      <div className="tt-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="tt-sheet-handle" />
        <div className="tt-sheet-header">
          <div className="eyebrow">{list?.length ?? 0} COMMENTS</div>
          <button type="button" className="icon-btn" onClick={onClose} data-testid="feed-comments-close">✕</button>
        </div>
        <div className="tt-sheet-body">
          {list === null && <div className="tiny" style={{ color: "rgba(255,255,255,0.4)", padding: 24 }}>Loading…</div>}
          {list?.length === 0 && <div className="tiny" style={{ color: "rgba(255,255,255,0.4)", textAlign: "center", padding: 24 }}>Be the first to comment.</div>}
          {list?.map((c) => (
            <div key={c.id} className="tt-comment-row" data-testid={`feed-comment-row-${c.id}`}>
              <div className="tt-mini-avatar">{c.user_name?.[0]?.toUpperCase() || "S"}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="body-sm" style={{ color: "#fff" }}>
                  <span style={{ fontWeight: 600 }}>@{c.user_name}</span>
                </div>
                <div className="body-sm" style={{ color: "rgba(255,255,255,0.82)" }}>{c.text}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="tt-sheet-footer">
          <input
            className="input" placeholder="Add a comment…" value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            data-testid="feed-comment-input"
            autoComplete="off"
          />
          <button className="btn btn-primary" onClick={send} disabled={!text.trim() || posting} data-testid="feed-comment-send">Post</button>
        </div>
      </div>
    </div>
  );
}

function PostMedia({ post, isActive, muted, onTogglePlay }) {
  const videoRef = useRef(null);
  const [showPlay, setShowPlay] = useState(false);
  const [manualPaused, setManualPaused] = useState(false);

  // Autoplay / pause based on active state
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isActive && !manualPaused) {
      v.currentTime = 0;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
      setShowPlay(false);
    } else {
      v.pause();
    }
  }, [isActive, manualPaused]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = muted;
  }, [muted]);

  if (post.media_type === "video" && post.media_url) {
    return (
      <div
        className="tt-media"
        onClick={() => {
          const v = videoRef.current;
          if (!v) return;
          if (v.paused) { v.play().catch(() => {}); setManualPaused(false); setShowPlay(false); }
          else { v.pause(); setManualPaused(true); setShowPlay(true); }
          onTogglePlay?.();
        }}
        data-testid={`feed-media-${post.id}`}
      >
        <video
          ref={videoRef}
          src={post.media_url}
          poster={post.poster_url || undefined}
          playsInline
          loop
          muted={muted}
          preload="metadata"
          className="tt-video"
        />
        {showPlay && (
          <div className="tt-play-overlay" aria-hidden="true">
            <Play size={56} strokeWidth={1.2} fill="rgba(255,255,255,0.9)" />
          </div>
        )}
      </div>
    );
  }

  const imgSrc =
    post.media_url ||
    (post.image_base64 ? `data:${post.image_mime || "image/jpeg"};base64,${post.image_base64}` : null);
  return (
    <div className="tt-media" data-testid={`feed-media-${post.id}`}>
      {imgSrc ? <img src={imgSrc} alt={post.caption || "fashion fit"} className="tt-image" /> : <div className="tt-image" />}
    </div>
  );
}

function ActionButton({ label, count, active, onClick, children, testId }) {
  return (
    <button
      type="button"
      className={`tt-action ${active ? "active" : ""}`}
      onClick={onClick}
      aria-label={label}
      data-testid={testId}
    >
      <span className="tt-action-icon">{children}</span>
      {count !== undefined && <span className="tt-action-count">{compactNum(count)}</span>}
    </button>
  );
}

function PostCard({ post, active, muted, currentUserId, onModerated, onLike, onSave, onShare, onComment, onFollow, onGetLook, onRemix, onOpenCreator, onToggleMute }) {
  return (
    <article className="tt-post" data-testid={`feed-post-${post.id}`}>
      <PostMedia post={post} isActive={active} muted={muted} onTogglePlay={onToggleMute} />

      {/* Top-right mute control (videos only) */}
      {post.media_type === "video" && (
        <button
          type="button"
          className="tt-mute"
          onClick={(e) => { e.stopPropagation(); onToggleMute(); }}
          aria-label={muted ? "Unmute" : "Mute"}
          data-testid={`feed-mute-${post.id}`}
        >
          {muted ? <VolumeX size={18} strokeWidth={1.8}/> : <Volume2 size={18} strokeWidth={1.8}/>}
        </button>
      )}

      {/* Right action rail */}
      <div className="tt-rail" data-testid={`feed-rail-${post.id}`}>
        <button
          type="button"
          className="tt-rail-avatar"
          onClick={() => onOpenCreator(post)}
          aria-label={`Open @${post.user_name}`}
          data-testid={`feed-creator-${post.id}`}
        >
          <span>{(post.user_name || "S")[0].toUpperCase()}</span>
          {!post.is_self && (
            <span
              className={`tt-rail-follow ${post.viewer_following ? "following" : ""}`}
              onClick={(e) => { e.stopPropagation(); onFollow(post); }}
              data-testid={`feed-follow-${post.id}`}
            >
              {post.viewer_following ? <UserCheck size={11} strokeWidth={2.4}/> : <UserPlus size={11} strokeWidth={2.4}/>}
            </span>
          )}
        </button>

        <ActionButton
          label="Like"
          count={post.likes_count}
          active={post.viewer_liked}
          onClick={() => onLike(post)}
          testId={`feed-like-${post.id}`}
        >
          <Heart size={30} fill={post.viewer_liked ? "currentColor" : "none"} strokeWidth={1.6}/>
        </ActionButton>

        <ActionButton
          label="Comment"
          count={post.comments_count}
          onClick={() => onComment(post)}
          testId={`feed-comment-${post.id}`}
        >
          <MessageCircle size={28} strokeWidth={1.7}/>
        </ActionButton>

        <ActionButton
          label="Save"
          count={post.saves_count}
          active={post.viewer_saved}
          onClick={() => onSave(post)}
          testId={`feed-save-${post.id}`}
        >
          <Bookmark size={28} fill={post.viewer_saved ? "currentColor" : "none"} strokeWidth={1.7}/>
        </ActionButton>

        <ActionButton
          label="Share"
          onClick={() => onShare(post)}
          testId={`feed-share-${post.id}`}
        >
          <Share2 size={28} strokeWidth={1.7}/>
        </ActionButton>

        {/* App Store 1.2 requirement — every UGC item has Report + Block affordances. */}
        <div style={{ display: "flex", justifyContent: "center", marginTop: 6 }}>
          <PostModerationMenu
            post={post}
            currentUserId={currentUserId}
            onModerated={onModerated}
          />
        </div>
      </div>

      {/* Bottom caption + CTAs */}
      <div className="tt-overlay-bottom">
        <button
          type="button"
          className="tt-username"
          onClick={() => onOpenCreator(post)}
          data-testid={`feed-user-${post.id}`}
        >
          @{post.user_name}
        </button>
        {post.caption && (
          <div className="tt-caption" data-testid={`feed-caption-${post.id}`}>{post.caption}</div>
        )}
        {post.style_tags?.length > 0 && (
          <div className="tt-tags">
            {post.style_tags.slice(0, 4).map((t) => (
              <span key={t} className="tt-tag">#{t}</span>
            ))}
          </div>
        )}
        <div className="tt-sound">
          <Music2 size={12} strokeWidth={2}/>
          <span>original audio · @{post.user_name}</span>
        </div>

        <div className="tt-cta-row">
          <button
            type="button"
            className="tt-cta tt-cta-shop"
            onClick={() => onGetLook(post)}
            data-testid={`feed-getlook-${post.id}`}
          >
            <ShoppingBag size={16} strokeWidth={2}/>
            <span>Get this look</span>
          </button>
          <button
            type="button"
            className="tt-cta tt-cta-remix"
            onClick={() => onRemix(post)}
            data-testid={`feed-remix-${post.id}`}
          >
            <Sparkles size={16} strokeWidth={2}/>
            <span>Remix fit</span>
          </button>
        </div>
      </div>
    </article>
  );
}

export default function FashionFeedScreen() {
  const navigate = useNavigate();
  const toast = useToast();
  const { update, reset } = useOutfitDraft();
  const { user } = useAuth();
  const currentUserId = user?.id;

  const [tab, setTab] = useState("fyp");
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const [activeComments, setActiveComments] = useState(null);
  const listRef = useRef(null);
  const itemRefs = useRef([]);
  const ranRef = useRef({ tab: null });
  const remixingRef = useRef(false);

  // Optimistically remove a moderated post (reported / owner blocked) from
  // the current view so the user gets instant feedback that the action worked.
  const handleModerated = useCallback((event) => {
    setPosts((prev) => {
      if (!prev) return prev;
      if (event.action === "report" && event.postId) {
        return prev.filter((p) => p.id !== event.postId);
      }
      if (event.action === "block" && event.userId) {
        return prev.filter((p) => p.user_id !== event.userId);
      }
      return prev;
    });
  }, []);

  const load = useCallback(async (nextTab) => {
    setPosts(null); setError(""); setActiveIndex(0);
    try {
      const params = new URLSearchParams();
      params.set("feed", nextTab);
      params.set("limit", "20");
      const { data } = await api.get(`/feed/posts?${params.toString()}`);
      setPosts(data.posts);
    } catch (err) {
      setError(formatApiError(err));
      setPosts([]);
    }
  }, []);

  useEffect(() => {
    if (ranRef.current.tab === tab) return;
    ranRef.current.tab = tab;
    load(tab);
  }, [tab, load]);

  // Track which item is currently visible (active = autoplay)
  useEffect(() => {
    if (!posts || posts.length === 0) return;
    const root = listRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        let bestIdx = activeIndex;
        let bestRatio = 0;
        for (const entry of entries) {
          const idx = Number(entry.target.dataset.idx);
          if (entry.intersectionRatio > bestRatio) {
            bestRatio = entry.intersectionRatio;
            bestIdx = idx;
          }
        }
        if (bestRatio >= 0.6) setActiveIndex(bestIdx);
      },
      { root, threshold: [0.4, 0.6, 0.8, 1] }
    );
    itemRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [posts]);

  const patchPost = (id, patch) =>
    setPosts((arr) => arr?.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const onLike = async (post) => {
    patchPost(post.id, {
      viewer_liked: !post.viewer_liked,
      likes_count: post.likes_count + (post.viewer_liked ? -1 : 1),
    });
    try { await api.post(`/feed/posts/${post.id}/like`); }
    catch { patchPost(post.id, { viewer_liked: post.viewer_liked, likes_count: post.likes_count }); }
  };
  const onSave = async (post) => {
    patchPost(post.id, {
      viewer_saved: !post.viewer_saved,
      saves_count: post.saves_count + (post.viewer_saved ? -1 : 1),
    });
    try {
      await api.post(`/feed/posts/${post.id}/save`);
      toast.show(post.viewer_saved ? "Removed" : "Saved", "success");
    } catch {
      patchPost(post.id, { viewer_saved: post.viewer_saved, saves_count: post.saves_count });
    }
  };
  const onShare = async (post) => {
    const url = `${window.location.origin}/feed?post=${post.id}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Fashion fit", text: post.caption || "fashion fit", url }); }
      catch { /* user cancelled share */ }
    } else {
      try { await navigator.clipboard.writeText(url); toast.show("Link copied", "success"); }
      catch { /* clipboard denied */ }
    }
  };
  const onFollow = async (post) => {
    if (!post.user_id) return;
    patchPost(post.id, { viewer_following: !post.viewer_following });
    try { await api.post(`/feed/users/${post.user_id}/follow`); }
    catch { patchPost(post.id, { viewer_following: post.viewer_following }); }
  };
  const onComment = (post) => setActiveComments(post);
  const onCommentCount = (id, delta) =>
    patchPost(id, { comments_count: (posts?.find((p) => p.id === id)?.comments_count || 0) + delta });

  // GET THIS LOOK = open the post's still image in Rate-My-Outfit critique flow.
  const onGetLook = (post) => {
    reset();
    const dataUrl =
      post.poster_url ||
      (post.image_base64 ? `data:${post.image_mime || "image/jpeg"};base64,${post.image_base64}` : null);
    if (!dataUrl) {
      toast.show("This post has no image to analyze.", "error");
      return;
    }
    update({ mode: "upload", imageBase64: dataUrl, imageMime: post.image_mime || "image/jpeg" });
    sessionStorage.setItem("sr_get_look_image", dataUrl);
    navigate("/rate?from=feed");
  };

  // REMIX THIS FIT = generate a fresh outfit using the CURRENT user's profile,
  // seeded by the post's style tags. Goes to /results.
  const onRemix = async (post) => {
    if (remixingRef.current) return;
    remixingRef.current = true;
    try {
      const { data } = await api.post(`/feed/posts/${post.id}/remix`, {});
      reset();
      update({
        mode: "budget",
        style: data.style || "trending",
        budget: data.budget != null ? String(data.budget) : "",
        gender: data.gender || "",
        imageBase64: null,
      });
      toast.show(`Remixing in ${data.style}…`, "success");
      navigate("/results");
    } catch (err) {
      toast.show(formatApiError(err) || "Couldn't remix. Try again.", "error");
    } finally {
      remixingRef.current = false;
    }
  };

  const onOpenCreator = (post) => {
    if (post.user_id) navigate(`/feed/u/${post.user_id}`);
  };

  const empty = useMemo(() => posts?.length === 0 && !error, [posts, error]);

  return (
    <div className="tt-feed" data-testid="feed-screen">
      {/* Top tab bar (TikTok-style) */}
      <header className="tt-topbar" data-testid="feed-tabs">
        <button
          type="button"
          className="tt-back"
          onClick={() => navigate("/home")}
          aria-label="Back"
          data-testid="feed-back-btn"
        >
          <ChevronLeft size={22} strokeWidth={2}/>
        </button>
        <div className="tt-tabs">
          {FEED_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`tt-tab ${tab === t.id ? "active" : ""}`}
              onClick={() => setTab(t.id)}
              data-testid={`feed-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="tt-back"
          onClick={() => navigate("/feed/new")}
          aria-label="New post"
          data-testid="feed-new-post-btn"
        >
          <Plus size={22} strokeWidth={2}/>
        </button>
      </header>

      {/* Feed viewport */}
      <div className="tt-list" ref={listRef} data-testid="feed-list">
        {posts === null && (
          <div className="tt-loading"><div className="spinner spinner-lg"/></div>
        )}

        {empty && (
          <div className="tt-empty" data-testid="feed-empty">
            <div className="eyebrow" style={{ marginBottom: 8 }}>NOTHING HERE YET</div>
            <p className="body-sm" style={{ color: "rgba(255,255,255,0.55)", marginBottom: 18 }}>
              {tab === "following"
                ? "Follow creators to see their fits here."
                : "Be the first to drop a fit in this feed."}
            </p>
            <button className="btn btn-primary" onClick={() => navigate("/feed/new")} data-testid="feed-empty-cta">
              <Plus size={14}/> Post a fit
            </button>
          </div>
        )}

        {error && (
          <div className="tt-empty" data-testid="feed-error">
            <div className="eyebrow" style={{ color: "var(--danger)", marginBottom: 8 }}>SOMETHING WENT WRONG</div>
            <p className="body-sm">{error}</p>
            <button className="btn btn-outline" onClick={() => load(tab)} data-testid="feed-retry-btn">Try again</button>
          </div>
        )}

        {posts?.map((p, idx) => (
          <div
            key={p.id}
            className="tt-slot"
            data-idx={idx}
            ref={(el) => (itemRefs.current[idx] = el)}
          >
            <PostCard
              post={p}
              active={idx === activeIndex}
              muted={muted}
              currentUserId={currentUserId}
              onModerated={handleModerated}
              onLike={onLike}
              onSave={onSave}
              onShare={onShare}
              onComment={onComment}
              onFollow={onFollow}
              onGetLook={onGetLook}
              onRemix={onRemix}
              onOpenCreator={onOpenCreator}
              onToggleMute={() => setMuted((m) => !m)}
            />
          </div>
        ))}
      </div>

      {activeComments && (
        <CommentsSheet
          post={activeComments}
          onCountChange={onCommentCount}
          onClose={() => setActiveComments(null)}
        />
      )}
    </div>
  );
}
